import React, {
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Input, InputGroup, InputRightElement, Text } from "@chakra-ui/react";

// 정수 금액에 천 단위 콤마를 넣는다 ("30000" → "30,000")
function formatWithCommas(raw: string): string {
  return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

interface PathPickPriceInputProps {
  // 서버 응답이 그대로 넘어올 수 있어 null도 받는다 — 미입력과 동일하게 빈 값으로 표시한다.
  value?: number | null;
  onChange: (value?: number) => void;
  // 입력창 안쪽 오른쪽에 붙는 단위 — 기본 "원"
  suffix?: ReactNode;
  placeholder?: string;
  isDisabled?: boolean;
}

// 패스픽 디자인 언어를 따르는 원화 금액 입력 — 천 단위 콤마 표시 + 단위 suffix.
function PathPickPriceInput({
  value,
  onChange,
  suffix = "원",
  placeholder = "0",
  isDisabled = false,
}: PathPickPriceInputProps) {
  // 표시용 문자열을 로컬로 들고 있어야 타이핑 중간 상태(콤마 위치)가 보존된다.
  const [displayValue, setDisplayValue] = useState<string>("");

  const inputRef = useRef<HTMLInputElement>(null);
  // 콤마 재포맷으로 커서가 끝으로 튀지 않도록, 리렌더 후 복원할 커서 위치
  const cursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (cursorRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorRef.current, cursorRef.current);
      cursorRef.current = null;
    }
  }, [displayValue]);

  // 외부 value가 로컬 표시값과 어긋나면(초기 시딩, 폼 리셋 등) 다시 맞춘다.
  useEffect(() => {
    // null/undefined는 모두 미입력으로 취급 — String(null)이 "null"로 표시되지 않도록 먼저 정규화한다.
    const normalizedValue = value ?? undefined;
    const parsed = parseInt(displayValue.replace(/,/g, ""), 10);
    const normalized = isNaN(parsed) ? undefined : parsed;
    if (normalized !== normalizedValue) {
      setDisplayValue(
        normalizedValue !== undefined
          ? formatWithCommas(String(normalizedValue))
          : ""
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const el = event.target;
    const raw = el.value.replace(/,/g, "");
    // 배송비는 음수·소수가 없으므로 숫자 외 입력은 무시한다.
    if (!/^\d*$/.test(raw)) {
      return;
    }

    const cursor = el.selectionStart ?? el.value.length;
    // 커서 왼쪽의 콤마 제외 문자 수를 기준으로, 재포맷된 문자열에서의 커서 위치를 계산
    const charsBeforeCursor = el.value
      .slice(0, cursor)
      .replace(/,/g, "").length;
    const formatted = formatWithCommas(raw);
    let pos = 0;
    let count = 0;
    while (pos < formatted.length && count < charsBeforeCursor) {
      if (formatted[pos] !== ",") count++;
      pos++;
    }

    if (formatted === displayValue) {
      // 콤마만 삭제된 경우: state가 그대로라 리렌더가 없으므로 DOM을 직접 복구
      el.value = formatted;
      el.setSelectionRange(pos, pos);
    } else {
      cursorRef.current = pos;
      setDisplayValue(formatted);
    }

    const parsed = parseInt(raw, 10);
    onChange(isNaN(parsed) ? undefined : parsed);
  };

  return (
    <InputGroup>
      <Input
        ref={inputRef}
        // 콤마를 담아야 하므로 number 타입을 쓸 수 없다.
        type={"text"}
        inputMode={"numeric"}
        value={displayValue}
        onChange={handleChange}
        placeholder={placeholder}
        isDisabled={isDisabled}
        fontSize={"13px"}
        // 판매자 기본 정보 설정 모달의 다른 입력 컨트롤과 높이를 맞춘다.
        height={"32px"}
        border={"0.5px solid #D4D4D1"}
        borderRadius={"8px"}
        background={"#FCFCFB"}
        _placeholder={{ color: "#8F8F8C" }}
      />
      {/* width를 지정해야 InputGroup이 입력값과 겹치지 않도록 paddingEnd를 잡아준다.
          height는 Input과 맞춰야 suffix가 세로 중앙에 온다. */}
      <InputRightElement pointerEvents={"none"} width={"40px"} height={"32px"}>
        <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
          {suffix}
        </Text>
      </InputRightElement>
    </InputGroup>
  );
}

export default PathPickPriceInput;
